import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PageHeader } from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import SpiceBowl from "@/components/SpiceBowl";
import {
  RefreshCw, Package, Sparkles, ShoppingBag, Ban,
  Plus, Loader2, MoreHorizontal, Wheat, Droplet, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import useStaplesData from "@/hooks/useStaplesData";

const TYPE_LABELS = {
  spice: "Spices",
  grain: "Grains & Flours",
  oil: "Oils & Fats",
  condiment: "Condiments",
  other: "Other",
};

const TYPE_ICON = {
  spice: Sparkles,
  grain: Wheat,
  oil: Droplet,
  condiment: FlaskConical,
  other: Package,
};

export default function Staples() {
  const {
    spices, others, presets, loading, loadingPreset,
    refill, setLevel, toggleStock, addCustom, loadPreset,
    load,
  } = useStaplesData();

  // Interaction state for a single jar
  const [selected, setSelected] = useState(null); // { staple, type }
  const [addOpen, setAddOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    name: "", staple_type: "spice", typical_lifespan_days: 90, color: "#b58a4a",
  });

  const submitAddCustom = async () => {
    const ok = await addCustom(newForm);
    if (ok) {
      setNewForm({ name: "", staple_type: "spice", typical_lifespan_days: 90, color: "#b58a4a" });
      setAddOpen(false);
    }
  };

  // Tracks a bowl that was just refilled so we can flash a "puff" animation
  // on the mound — feels like a spoonful of fresh powder just landed.
  const [puffId, setPuffId] = useState(null);
  // Ambient sparkle: every ~12s one random well-stocked bowl glimmers for
  // ~900ms so the drawer feels like a living surface, not a static grid.
  const [sparkleId, setSparkleId] = useState(null);

  useEffect(() => {
    // Only twinkle when there's something to twinkle over.
    if (spices.length < 2) return undefined;
    const eligible = spices.filter((s) => !s.is_out_of_stock && (s.fill_level ?? 0) > 0.2);
    if (eligible.length === 0) return undefined;
    const pickAndSparkle = () => {
      const pick = eligible[Math.floor(Math.random() * eligible.length)];
      setSparkleId(pick.id);
      setTimeout(() => setSparkleId((cur) => (cur === pick.id ? null : cur)), 950);
    };
    // Stagger the first sparkle by 3–8s so it doesn't fire the moment the
    // page loads, then loop every 10–14s.
    const firstDelay = 3000 + Math.random() * 5000;
    const first = setTimeout(pickAndSparkle, firstDelay);
    const interval = setInterval(pickAndSparkle, 10000 + Math.random() * 4000);
    return () => { clearTimeout(first); clearInterval(interval); };
  }, [spices]);

  // Wrap the hook's `refill` so we can trigger the puff animation on
  // top of the actual mutation.
  const handleRefill = async (id) => {
    const ok = await refill(id);
    if (ok) {
      setPuffId(id);
      setTimeout(() => setPuffId((cur) => (cur === id ? null : cur)), 900);
    }
  };

  const empty = !loading && spices.length === 0 && others.length === 0;

  // Group non-spice staples by type for the pantry-shelf below the drawer.
  const groupedOthers = others.reduce((acc, o) => {
    const t = o.staple_type || "other";
    (acc[t] = acc[t] || []).push(o);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="STAPLES & SPICES"
        title="The pantry drawer."
        subtitle="Everything you always have on hand — turmeric, salt, olive oil. HearthHouse assumes these are in stock when suggesting recipes, until you say otherwise."
        icon={Package}
        action={
          <Button
            data-testid="staples-add-btn"
            onClick={() => setAddOpen(true)}
            className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white px-5 h-10"
          >
            <Plus size={13} className="mr-1.5"/> Add a staple
          </Button>
        }
      />

      {/* Empty state — region picker */}
      {empty && (
        <div data-testid="staples-onboarding" className="warm-card p-8">
          <div className="max-w-2xl">
            <p className="caption text-hearth-clayDark mb-2">A soft place to start</p>
            <h2 className="font-display text-3xl md:text-4xl text-hearth-ink">
              Stock the drawer.
            </h2>
            <p className="text-hearth-mute mt-2 leading-relaxed">
              Pick the shelf that matches your kitchen and we&apos;ll set it up in one tap.
              You can add anything else by hand.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="staples-preset-grid">
            {presets.map((p) => (
              <div
                key={p.key}
                data-testid={`preset-card-${p.key}`}
                className={`rounded-2xl border border-hearth-line bg-hearth-paper p-5 transition ${
                  loadingPreset === p.key
                    ? "opacity-70 pointer-events-none"
                    : loadingPreset ? "opacity-60 pointer-events-none" : "hover:shadow-md"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-xl text-hearth-ink">{p.label}</h3>
                  <span className="caption text-hearth-mute shrink-0">{p.count}</span>
                </div>
                <p className="text-sm text-hearth-mute mt-2 leading-relaxed min-h-[3rem]">
                  {p.description}
                </p>
                <Button
                  data-testid={`preset-load-${p.key}`}
                  onClick={() => loadPreset(p.key)}
                  disabled={loadingPreset === p.key}
                  className="mt-4 rounded-full bg-hearth-ink hover:bg-black text-hearth-cream h-9 px-4 text-sm w-full"
                >
                  {loadingPreset === p.key
                    ? <Loader2 size={13} className="mr-1.5 animate-spin"/>
                    : <ShoppingBag size={13} className="mr-1.5"/>}
                  Load into my drawer
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="warm-card p-10 text-center">
          <Loader2 size={22} className="animate-spin mx-auto text-hearth-clay"/>
          <p className="mt-2 caption">Opening the drawer…</p>
        </div>
      )}

      {/* Spice Drawer — dark walnut bazaar table with top-down clay bowls */}
      {!empty && !loading && spices.length > 0 && (
        <section data-testid="spice-drawer" className="staples-cabinet">
          <div className="staples-cabinet__frame">
            <div className="staples-cabinet__topbar">
              <div>
                <div className="staples-cabinet__eyebrow">
                  Spice Drawer · {spices.length} {spices.length === 1 ? "spice" : "spices"}
                </div>
                <div className="staples-cabinet__caption mt-1">
                  the pantry, today.
                </div>
              </div>
              {presets.length > 0 && (
                <button
                  data-testid="spice-drawer-layer-cta"
                  onClick={() => document.querySelector("[data-testid='staples-more-presets']")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  className="staples-cabinet__layer-cta"
                >
                  + layer another region
                </button>
              )}
            </div>
            <div className="staples-cabinet__drawer">
              <div className="staples-cabinet__wood">
                <div className="staples-cabinet__grid" data-testid="spice-drawer-grid">
                  {spices.map((s) => (
                    <SpiceBowl
                      key={s.id}
                      name={s.name}
                      fill={s.fill_level}
                      color={s.color || "#b58a4a"}
                      outOfStock={s.is_out_of_stock}
                      puff={puffId === s.id}
                      sparkle={sparkleId === s.id}
                      onClick={() => setSelected({ staple: s, type: "spice" })}
                      data-testid={`spice-jar-${s.id}`}
                    />
                  ))}
                  {presets.length > 0 && (
                    <button
                      onClick={() => document.getElementById("staples-add-more")?.scrollIntoView({ behavior: "smooth" })}
                      className="flex flex-col items-center justify-center gap-2 rounded-full border-2 border-dashed border-hearth-cream/30 text-hearth-cream/60 hover:text-hearth-cream/90 hover:border-hearth-cream/60 transition"
                      style={{ width: 140, height: 100 }}
                      data-testid="spice-add-more"
                    >
                      <Plus size={20}/>
                      <span className="text-xs italic">add more</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other staples — grains, oils, condiments — a warm list below the drawer */}
      {!empty && Object.keys(groupedOthers).length > 0 && (
        <section id="staples-add-more" className="space-y-6" data-testid="staples-others">
          {Object.entries(groupedOthers).map(([type, items]) => {
            const Icon = TYPE_ICON[type] || Package;
            return (
              <div key={type} className="warm-card p-6" data-testid={`staples-group-${type}`}>
                <div className="flex items-center gap-2 caption text-hearth-clayDark mb-3">
                  <Icon size={14}/> {TYPE_LABELS[type] || type}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {items.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setSelected({ staple: o, type: "other" })}
                      data-testid={`staple-row-${o.id}`}
                      className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition ${
                        o.is_out_of_stock
                          ? "bg-hearth-coral/8 border border-hearth-coral/40"
                          : o.fill_level < 0.3
                          ? "bg-hearth-clay/10 border border-hearth-clay/30"
                          : "bg-hearth-stone/40 border border-hearth-line hover:border-hearth-clayDark"
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-hearth-ink truncate">{o.name}</div>
                        <div className={`text-[10px] italic ${
                          o.is_out_of_stock ? "text-hearth-coral" :
                          o.fill_level < 0.25 ? "text-hearth-coral" :
                          "text-hearth-mute"
                        }`}>
                          {o.is_out_of_stock ? "out of stock" :
                           o.fill_level < 0.15 ? "almost gone" :
                           o.fill_level < 0.4 ? "running low" :
                           o.fill_level < 0.75 ? "half full" : "full"}
                        </div>
                      </div>
                      <div
                        className="w-2 h-8 rounded-full overflow-hidden bg-hearth-stone shrink-0"
                        title={`${Math.round((o.fill_level || 0) * 100)}%`}
                      >
                        <div
                          style={{
                            height: `${Math.max(4, Math.round((o.fill_level || 0) * 100))}%`,
                            marginTop: "auto",
                            background: o.color || "#b58a4a",
                          }}
                          className="w-full block"
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {/* If drawer is non-empty but they want more, offer preset packs again */}
      {!empty && presets.length > 0 && (
        <section className="warm-card p-6 mt-4" data-testid="staples-more-presets">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <div>
              <p className="caption text-hearth-clayDark">Layer on another region</p>
              <h3 className="font-display text-xl text-hearth-ink">Load another pantry</h3>
            </div>
            <p className="text-xs italic text-hearth-mute">already-owned items are skipped</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <Button
                key={p.key}
                data-testid={`preset-load-more-${p.key}`}
                onClick={() => loadPreset(p.key)}
                disabled={loadingPreset !== null}
                variant="ghost"
                className="rounded-full border border-hearth-line hover:bg-hearth-stone text-sm h-9"
              >
                {loadingPreset === p.key
                  ? <Loader2 size={12} className="mr-1.5 animate-spin"/>
                  : null}
                {p.label} · {p.count}
              </Button>
            ))}
          </div>
        </section>
      )}

      {/* Actions dialog: single jar/row taps */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-sm">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{selected.staple.name}</DialogTitle>
                <DialogDescription className="text-hearth-mute">
                  Currently {selected.staple.is_out_of_stock ? "out of stock" :
                    `~${Math.round((selected.staple.fill_level || 0) * 100)}% full`}.
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                  data-testid="staple-action-refill"
                  onClick={async () => { await handleRefill(selected.staple.id); setSelected(null); }}
                  className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white h-10 text-sm col-span-2"
                >
                  <RefreshCw size={13} className="mr-1.5"/> I refilled this
                </Button>
                <Button
                  data-testid="staple-action-half"
                  variant="ghost"
                  onClick={async () => { await setLevel(selected.staple.id, 0.5); setSelected(null); }}
                  className="rounded-full border border-hearth-line hover:bg-hearth-stone h-9 text-xs"
                >
                  Half full
                </Button>
                <Button
                  data-testid="staple-action-low"
                  variant="ghost"
                  onClick={async () => { await setLevel(selected.staple.id, 0.2); setSelected(null); }}
                  className="rounded-full border border-hearth-line hover:bg-hearth-stone h-9 text-xs"
                >
                  Running low
                </Button>
                <Button
                  data-testid="staple-action-toggle-stock"
                  variant="ghost"
                  onClick={async () => { await toggleStock(selected.staple.id); setSelected(null); }}
                  className={`rounded-full border h-9 text-xs col-span-2 ${
                    selected.staple.is_out_of_stock
                      ? "border-hearth-sage/50 text-hearth-sageDark hover:bg-hearth-sage/10"
                      : "border-hearth-coral/50 text-hearth-coral hover:bg-hearth-coral/10"
                  }`}
                >
                  <Ban size={13} className="mr-1.5"/>
                  {selected.staple.is_out_of_stock ? "It's back in stock" : "It's out of stock (add to grocery)"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add a custom staple */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add a staple</DialogTitle>
            <DialogDescription className="text-hearth-mute">
              Something you always keep on hand that&apos;s not on the preset shelves.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="caption text-hearth-mute">Name</Label>
              <Input
                data-testid="staples-add-name"
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="e.g. Za'atar"
                className="input-warm mt-1.5 h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="caption text-hearth-mute">Type</Label>
                <Select value={newForm.staple_type}
                        onValueChange={(v) => setNewForm({ ...newForm, staple_type: v })}>
                  <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="staples-add-type">
                    <SelectValue/>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spice">Spice (jar)</SelectItem>
                    <SelectItem value="grain">Grain / flour</SelectItem>
                    <SelectItem value="oil">Oil / fat</SelectItem>
                    <SelectItem value="condiment">Condiment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="caption text-hearth-mute">Lifespan (days)</Label>
                <Input
                  data-testid="staples-add-lifespan"
                  type="number" min={7} max={730}
                  value={newForm.typical_lifespan_days}
                  onChange={(e) => setNewForm({ ...newForm, typical_lifespan_days: e.target.value })}
                  className="input-warm mt-1.5 h-11"
                />
              </div>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Colour (spice powder tone)</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <input
                  data-testid="staples-add-color"
                  type="color"
                  value={newForm.color}
                  onChange={(e) => setNewForm({ ...newForm, color: e.target.value })}
                  className="w-14 h-11 rounded-2xl border border-hearth-line cursor-pointer"
                />
                <span className="text-sm text-hearth-mute">Tap to pick — this is the fill inside your jar.</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="staples-add-save"
              onClick={submitAddCustom}
              disabled={!newForm.name.trim()}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white"
            >
              Add to drawer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
